import { css } from 'styled-components';
import { breakpointsMedia } from '@nubank/nuds-web/styles/breakpoints';
import { isType } from '@nubank/nuds-web/utils/isType/isType';

import { requireImage } from '../../../utils/requireImage';

import isExternalImageSource from './isExternalImageSource';
import addWebpBackgroundImage, { handleNotSupportedWebp } from './addWebpBackgroundImage';

const webpDefaultValue = true;

const handleBackground = (imagePath, webp = webpDefaultValue) => {
  const isImagePathNone = imagePath === 'none';
  if (isImagePathNone) {
    return `
      background-image: none;

      ${handleNotSupportedWebp(`
        background-image: none;
      `)}
    `;
  }

  if (isExternalImageSource(imagePath)) {
    return `background-image: url(${imagePath});`;
  }

  return (webp
    ? addWebpBackgroundImage(imagePath)
    : `background-image: url(${requireImage(imagePath)});`
  );
};

function createDPR(dpr) {
  return {
    pixelRatio: dpr,
    dpi: 96 * dpr,
    ddpx: dpr,
  };
}
const DPRValuesByDensity = {
  '1x': createDPR(1),
  '2x': createDPR(2),
  '3x': createDPR(3),
};

function handlePixelRatioBreakpoints(srcSetDPR, webp = webpDefaultValue) {
  const has1xImage = srcSetDPR['1x'];
  if (!has1xImage) {
    throw new Error('You must provide the "1x" image to be used as default');
  }

  const breakpointDPRs = Object.entries(srcSetDPR);

  return css`
    ${breakpointDPRs.map(([currentDPR, currentImagePath]) => {
    const currentDensityDPRValues = DPRValuesByDensity[currentDPR];
    return css`
      @media
      only screen and (-webkit-min-device-pixel-ratio: ${currentDensityDPRValues.pixelRatio}),
      only screen and (        min-device-pixel-ratio: ${currentDensityDPRValues.pixelRatio}),
      only screen and (                min-resolution: ${currentDensityDPRValues.dpi}dpi),
      only screen and (                min-resolution: ${currentDensityDPRValues.ddpx}dppx) {
        ${handleBackground(currentImagePath, webp)}
      }
    `;
  })}
  `;
}

function handleBackgroundWithDPR(srcSetItem, webp = webpDefaultValue) {
  const isPixelRatioBreakpointPath = !isType('string', srcSetItem);
  return isPixelRatioBreakpointPath
    ? handlePixelRatioBreakpoints(srcSetItem, webp)
    : handleBackground(srcSetItem, webp);
}

export default function backgroundImage(srcSet, webp = webpDefaultValue) {
  if (isType('string', srcSet)) {
    return handleBackground(srcSet, webp);
  }

  return breakpointsMedia({
    ...(
      srcSet.xs && ({
        xs: css`
          ${handleBackgroundWithDPR(srcSet.xs, webp)}
        `,
      })
    ),
    ...(
      srcSet.sm && ({
        sm: css`
          ${handleBackgroundWithDPR(srcSet.sm, webp)}
        `,
      })
    ),
    ...(
      srcSet.md && ({
        md: css`
          ${handleBackgroundWithDPR(srcSet.md, webp)}
        `,
      })
    ),
    ...(
      srcSet.lg && ({
        lg: css`
          ${handleBackgroundWithDPR(srcSet.lg, webp)}
        `,
      })
    ),
    ...(
      srcSet.xl && ({
        xl: css`
          ${handleBackgroundWithDPR(srcSet.xl, webp)}
        `,
      })
    ),
  });
}
